import { createSelector } from 'reselect';
import { flatten, values, memoize, merge } from 'lodash';
import {
  buildsById,
  buildIdsByJobId,
  getConcerningTrendingOnly,
  getCurrentDispatcherTestsOnly,
  getDispatcherType,
  getPanelsOpenStatus,
  getSearchTerm,
  groupScenariosByDispatcherId,
  metricsByJobIds,
  paramsByJobIds,
  regressionPageReducer
} from '../reducers';
import {
  commonPageInfo as commonPageHelpers,
  dispatcherTypes as dispatcherTypeHelpers,
  installer as installerHelpers,
  params as paramHelpers,
  regressionPages as regressionPageHelpers,
  scenarios as scenarioHelpers
} from '../helpers';
import {
  getDispatcherIds,
  getDispatcherJobInfo,
  getDispatcherJobsByDispIds
} from './dispatcher';
import * as dispatcherSelectors from './dispatcher';
import moment from 'moment';

/**
 * Returns array of all scenarios expected to be recent for display,
 * mapped from array of recent dispatcher ids from within
 * Regression's jobInfo
 */
export const getRegressionRecentScenarios = createSelector(
  getDispatcherIds,
  groupScenariosByDispatcherId,
  (recentDispatcherIds, scenariosByDispatcherId) => {
    const recentScenarios = flatten(recentDispatcherIds.sort((a, b) => b - a)
      .map(id => scenariosByDispatcherId[id])
      .filter(s => s));
    return recentScenarios;
  }
);

/**
 * Consumes recent Regression scenarios and finds past results for
 * the same scenarios to build the trending results array and scoring
 */
export const getPastScenarioTrends = createSelector(
  getRegressionRecentScenarios,
  getDispatcherJobsByDispIds,
  paramsByJobIds,
  buildsById,
  buildIdsByJobId,
  regressionPageReducer.getRegressionPage,
  getPanelsOpenStatus,
  (
    pastScenarios,
    dispatcherJobsByDispIds,
    params,
    buildsByIds,
    buildIdsByJobIds,
    regressionPageInfo,
    panelsOpenStatus
  ) => {
    if (!panelsOpenStatus[commonPageHelpers.PANELS.TRENDING]) {
      return [];
    }

    const scenarios = pastScenarios.reduce(
      (result, scenario) => {
        const dispatcherJob = dispatcherJobsByDispIds &&
          dispatcherJobsByDispIds[scenario.dispatcher];
        const dispatcherJobNameNumber = dispatcherJob &&
          `${dispatcherJob.name}_${dispatcherJob.number}`;

        let isTopic = false;
        if (buildIdsByJobIds[scenario.runner] &&
          buildsByIds[buildIdsByJobIds[scenario.runner][0]]) {
          isTopic = buildsByIds[buildIdsByJobIds[scenario.runner][0]].is_topic;
        }

        const runnerJobParams = params[scenario.runner] || {};

        // decide if we need to include this scenario
        if (!regressionPageHelpers.filterScenario(
          paramHelpers.getJobProductVersions(runnerJobParams),
          isTopic,
          regressionPageInfo.filters,
        )) {
          return result;
        }

        // parent object for single scenario row in the Trending table
        const scenarioContext = {
          name: scenario.name,
          score: 0,
          timestamp: '',
          trendingResults: [],
          dispatcherNameNumber: dispatcherJobNameNumber,
        };

        // first time seeing scenario in the result? add in scenarioContext
        if (result[scenario.name] === undefined) {
          result[scenario.name] = scenarioContext;
        }

        // push current scenario context to trendingResults
        result[scenario.name].trendingResults.push({
          updatedAt: scenario.updated_at,
          status: scenario.status,
          dispatcherNameNumber: dispatcherJobNameNumber,
          runner: scenario.runner,
          productVersions: paramHelpers.getJobProductVersions(runnerJobParams),
          isTopic
        });

        // scores and timestamp used for trend order
        if (result[scenario.name].trendingResults.length <= 5) {
          if (scenario.status === scenarioHelpers.SCENARIO_FAILED) {
            result[scenario.name].score++;
            const scenarioTime = scenario.created_at;
            const currentTime = Date.parse(result[scenario.name].timestamp) || 0;
            if (Date.parse(scenarioTime) > currentTime) {
              result[scenario.name].timestamp = scenario.created_at;
            }
          } else if (scenario.status === scenarioHelpers.SCENARIO_RUNNING) {
            result[scenario.name].score += 0.5;
          }
        }
        return result;
      }, {});
    const scenarioContextsWithTrends = values(scenarios);
    return scenarioContextsWithTrends;
  }
);

/**
 * Takes in the scenarios dispatched by the currently viewed dispatcher on a
 * Regression page and concats it with the given set of relevant scored scenarios
 * that were run in the past
 */
export const getRegressionRecentAndPastScenarios = createSelector(
  (state) => getDispatcherJobInfo(state).dispatcherId,
  groupScenariosByDispatcherId,
  getPastScenarioTrends,
  (dispatcherId, scenariosByDispatcherId, pastScenarios) => {
    const result = [
      ...(scenariosByDispatcherId[dispatcherId] || []),
      ...pastScenarios
    ];
    return result;
  }
);


/**
 * Takes in the set of all possible scored scenarios to display and applies
 * the search term filter to correctly reduce the set.
 * If on the regression page then also apply an additional targetted filter
 */
export const getAllConcerningRegressionScenarios = createSelector(
  getDispatcherType,
  getRegressionRecentAndPastScenarios,
  getSearchTerm,
  paramsByJobIds,
  (dispatcherType, recentAndPastScenarios, rawSearchTerm, paramsByJobId) => {
    if (rawSearchTerm.length === 0) {
      return recentAndPastScenarios;
    }

    const searchTerm = rawSearchTerm.toLocaleLowerCase();

    if (dispatcherType === dispatcherTypeHelpers.INSTALLERTESTS) {
      return recentAndPastScenarios.filter(s => {
        const params = paramsByJobId[s.runner] || {};
        return params[paramHelpers.INSTALL_SUCCESS] &&
          params[paramHelpers.INSTALL_SUCCESS]
            .toLocaleLowerCase()
            .indexOf(searchTerm) !== -1;
      });
    } else {
      return recentAndPastScenarios
        .filter(s => s.name.toLocaleLowerCase().indexOf(searchTerm) !== -1);
    }
  }
);

/**
 * Extracts the scenario runner ids for scenarios that are considered trending
 * for use in the Trending panel on Regression pages
 * If the panel is closed (default), this will exclude the calculation and
 * simply return empty array to avoid unnecessary resource use
 */
export const getTrendingRunnerIds = createSelector(
  getRegressionRecentAndPastScenarios,
  getPanelsOpenStatus,
  (recentAndPastScenarios, panelsOpenStatus) => {
    if (!panelsOpenStatus[commonPageHelpers.PANELS.TRENDING]) {
      return [];
    }

    const trendingRunnerIds = flatten(recentAndPastScenarios
      .filter(s => s.trendingResults)
      .map(scenario => {
        return scenario.trendingResults
          .slice(0, 5)
          .map(r => r.runner);
      })
    ).filter(x => x);
    return trendingRunnerIds;
  });

/**
 * Consumes all concerning scenarios and sorts them into buckets which will
 * be the panels for display.  Trending scenarios are determined by their score
 * and the remaining results are determined by the scenario's status
 */
// The toggle values are consumed because the selector needs to recalculate
// when the toggles change, however they are otherwise not used
export const regressionConcerningScenarioByStatus = createSelector(
  getConcerningTrendingOnly,
  getCurrentDispatcherTestsOnly,
  getDispatcherType,
  getAllConcerningRegressionScenarios,
  (co, cdo, dispatcherType, concerningScenarios) => {
    if (dispatcherType === dispatcherTypeHelpers.INSTALLERTESTS) {
      return {};
    }

    const resultConcerning = concerningScenarios.reduce(
      (result, scenario) => {
        if (scenario.trendingResults) {
          if (scenario.score < 4.5) {
            result[scenarioHelpers.TRENDING] =
              [...(result[scenarioHelpers.TRENDING] || []), scenario];
          } else {
            result[scenarioHelpers.TRENDING_FAILURE] =
              [...(result[scenarioHelpers.TRENDING_FAILURE] || []), scenario];
          }
          return result;
        }

        result[scenario.status] =
          [...(result[scenario.status] || []), scenario];
        return result;
      }, {});
    return resultConcerning;
  }
);

/**
 * A special variant for Installer Regression scenarios
 * Consumes all concerning scenarios and sorts them into buckets which will
 * be the panels for display.  Trending scenarios are determined by their score
 * and the remaining results are determined by the test and file compare results
 */
// The toggle values are consumed because the selector needs to recalculate
// when the toggles change, however they are otherwise not used
export const installerConcerningScenariosByStatus = createSelector(
  getConcerningTrendingOnly,
  getCurrentDispatcherTestsOnly,
  getDispatcherType,
  getAllConcerningRegressionScenarios,
  metricsByJobIds,
  (co, cdo, dispatcherType, concerningScenarios, metricsByJobIds) => {
    if (dispatcherType !== dispatcherTypeHelpers.INSTALLERTESTS) {
      return {};
    }

    const resultConcerning = concerningScenarios.reduce(
      (result, scenario) => {
        const metrics = metricsByJobIds[scenario.runner];

        if (scenario.trendingResults) {
          if (scenario.score < 5) {
            result[scenarioHelpers.TRENDING] =
              [...(result[scenarioHelpers.TRENDING] || []), scenario];
          } else {
            result[scenarioHelpers.TRENDING_FAILURE] =
              [...(result[scenarioHelpers.TRENDING_FAILURE] || []), scenario];
          }
          return result;
        }

        const scenarioVerdict =
          installerHelpers.scenarioVerdictFromMetrics(metrics, scenario.status);

        result[scenarioVerdict] =
          [...(result[scenarioVerdict] || []), scenario];

        return result;
      }, {});
    return resultConcerning;
  }
);

/**
 * Based on the values of the checkboxes within the given panel,
 * this will apply the respective filters on the scenarios.
 * If the panel is closed (default), this will exclude the calculation and
 * simply return empty array to avoid unnecessary resource use
 */
export const filterRegressionScenariosForDisplay = createSelector(
  getDispatcherJobInfo,
  groupScenariosByDispatcherId,
  getConcerningTrendingOnly,
  getCurrentDispatcherTestsOnly,
  (
    dispatcherJobInfo,
    groupScenariosByDispatcherId,
    isConcerningOnly,
    isCurrentDispatchTestsOnly
  ) => memoize(({ scenariosByStatus, panel }) => {
    if (!scenariosByStatus[panel]) {
      return scenariosByStatus;
    }

    const filtered = scenariosByStatus;
    if (isConcerningOnly) {
      filtered[panel] = filtered[panel].filter(s => s.score > 0);
    }

    if (isCurrentDispatchTestsOnly) {
      const dispatchedScenarios =
        groupScenariosByDispatcherId[dispatcherJobInfo.dispatcherId] || [];
      const scenarioNameLookup = dispatchedScenarios.reduce((result, scenario) => {
        result[scenario.name] = scenario;
        return result;
      }, {});

      filtered[panel] = filtered[panel]
        .filter(s => !!scenarioNameLookup[s.name]);
    }

    // sort trending before returning
    filtered[panel] = filtered[panel].sort((a, b) =>
      b.score - a.score || Date.parse(b.timestamp) - Date.parse(a.timestamp));

    return filtered;
  })
);

/**
 * Consumes the concerning scenarios for display into each Regression panel
 * and merges into a final object with the trending panel filters also applied
 */
export const regressionScenariosForDisplayByPanel = createSelector(
  regressionConcerningScenarioByStatus,
  installerConcerningScenariosByStatus,
  getDispatcherType,
  filterRegressionScenariosForDisplay,
  (
    regressionScenarios,
    installerScenarios,
    dispatcherType,
    filterRegressionScenariosForDisplay,
  ) => {

    // scenarios to use is dependent on the current page
    const scenariosByStatus = dispatcherType === dispatcherTypeHelpers.INSTALLERTESTS ?
      installerScenarios :
      regressionScenarios;

    return merge(
      {},
      filterRegressionScenariosForDisplay({
        scenariosByStatus,
        panel: scenarioHelpers.TRENDING
      }),
      filterRegressionScenariosForDisplay({
        scenariosByStatus,
        panel: scenarioHelpers.TRENDING_FAILURE
      })
    );
  }
);

/**
 * Based on ALL the dispatcher data scoped to the current Regression page context,
 * return an array of all scenarios that ran.
 * Sorted by created_at asc
 */
export const regressionAllScenarios = createSelector(
  getDispatcherJobInfo,
  dispatcherSelectors.recentDispatchersAndJobsByName,
  groupScenariosByDispatcherId,
  (regressionSelectorInfo, allDispatchersAndJobsByName, scenariosByDispatcherId) => {
    const relevantDispatchers = allDispatchersAndJobsByName
      .dispatchers[regressionSelectorInfo.jobName] || [];
    const allDispatcherIds = relevantDispatchers.map(d => d.id);

    const allScenarios = allDispatcherIds.reduce(
      (result, id) => result.concat(scenariosByDispatcherId[id] || []),
      []
    );

    return allScenarios.sort((a, b) => moment(a.created_at) - moment(b.created_at));
  }
);

/**
 * Sort all scenarios of Regression page context by their scenario reason text
 * for scenarios that are in a final negative state
 */
export const regressionFailureTrendsScenariosByReason = createSelector(
  regressionAllScenarios,
  (allScenarios) => {
    const negativeStates = [
      scenarioHelpers.SCENARIO_ERROR,
      scenarioHelpers.SCENARIO_FAILED
    ];
    const negativeScenarios = allScenarios.reduce((acc, scenario) => {
      if (negativeStates.includes(scenario.status)) {
        // reasons are formatted '<prettyReasonType> | <detailedReason>'
        const reason = scenario.reason ?
          scenario.reason.split('|')[0].trim() :
          scenarioHelpers.NO_REASON_FOUND;

        acc[reason] = acc[reason] ?
          [ ...acc[reason], scenario ] :
          [ scenario ];
        return acc;
      }
      return acc;
    }, {});
    return negativeScenarios;
  }
);


// WEBPACK FOOTER //
// src/selectors/scenario.js